import { Component, ElementRef, EventEmitter, Output, ViewChild } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, concatAll, concatMap, map } from "rxjs/operators";
import { DatePipe } from "@angular/common";

import { IAPIRx } from "../../../app/API/api.base";
import { NAService } from "../../../app/API/na.service";
import { IVirtualDeviceRxData } from "../../../app/API/v1/VirtualDevice/virtualDevice.common";
import { DeviceInfo, OnlineStatus } from "../../../app/content/device/data/device-info";
import { VirtualDeviceActions, VirtualDeviceStatusType, VirtualDeviceUIInfo } from "../../../app/content/device/data/virtual-device-info";
import { DeviceService } from "../../../app/content/device/device.service";
import { DEVICE_GROUP_NAME_DEFAULT, DeviceGroupInfo } from "../../../app/content/device/group/dev-group.data";
import { DeviceGroupService } from "../../../app/content/device/group/dev-group.service";
import { AccountService } from "../../../app/entry/account.service";
import { DialogPage } from "../../../app/lib/common/common.data";
import { ConstantService } from "../../../app/lib/common/constant.service";
import { FileReaderParseType, HelperLib } from "../../../app/lib/common/helper.lib";
import { VirtualDlgComponent } from "../../../app/content/virtual/dlg/virtual-dlg.component";

@Component({
    selector: 'na-dev-bulk-register',
    templateUrl: './dev-bulk-register.component.html',
    styles: [
        `.btn-link {
  text-decoration: underline;
}
  .list-group-content {
  max-height: calc(100vh - 280px);
}`
    ],
    standalone: false
})
export class DeviceBulkRegisterComponent extends VirtualDlgComponent {
    readonly STATUS_SUCCESS: string = 'Success';
    readonly STATUS_FAIL: string = 'Failed';
    readonly TEMPLATE_ROW: string[] = ['Device name', 'MAC', 'Pairing code', 'Device group name or path'];
    readonly TEMPLATE_ERROR: string = 'Template is not match or invalid data';

    _dlgPage: DialogPage = DialogPage.prepare;
    _enumDlgPage: typeof DialogPage = DialogPage;
    _batchPairProgress: string;
    _batchPairInfos: { deviceName: string, mac: string, pairingCode: string, devGroupPath?: string, devGroupId: string, toGroupPath?: string, note?: string, isFault: boolean }[] = [];
    _displayBatchPairInfos: { deviceName: string, mac: string, pairingCode: string, devGroupPath?: string, devGroupId: string, note?: string, isFault: boolean }[] = [];
    _filters: Map<string, boolean> = new Map([[this.STATUS_SUCCESS, true], [this.STATUS_FAIL, true]]);

    _errorMessage: string;

    @ViewChild('bulkTemplateLink') private _bulkTemplateRef: ElementRef;
    @ViewChild('filePicker') private _filePickerRef: ElementRef;
    @ViewChild('bulkResultLink') private _bulkResulteRef: ElementRef;

    @Output() onDevicePaired: EventEmitter<VirtualDeviceUIInfo> = new EventEmitter<VirtualDeviceUIInfo>();

    constructor(
        private naSvc: NAService,
        private constantSvc: ConstantService,
        protected accountSvc: AccountService,
        private devSvc: DeviceService,
        private devGroupSvc: DeviceGroupService,
        private datePipe: DatePipe,
    ) { 
        super(accountSvc);
    }

    beginBulkRegister(): void {
        this._dlgPage = DialogPage.action;
        this._batchPairInfos = this._displayBatchPairInfos = [];
        this._batchPairProgress = '';
    }

    downloadBulkTemplate(): void {
        of({ fileName: 'deviceRegisterTemplate', dataList: [this.TEMPLATE_ROW] }).pipe(
            map((c: { fileName: string, header: string, metadata?: string[], dataList: string[][], date: string }) => {
                return HelperLib.downloadCsv(this._bulkTemplateRef.nativeElement, c.fileName, c.header, c.dataList, c.metadata);
            })
        ).subscribe();
    }

    importBulkRegisterContent(): void {
        this._errorMessage = '';
        this._filePickerRef.nativeElement.click();
    }

    downloadBulkResult(): void {
        of({ fileName: 'deviceRegisterResult_' + this.datePipe.transform(new Date(), 'yyyy-MM-dd HH_mm_ss'), dataList: [['Device name', 'MAC', 'Register status', 'Register device group', 'Note']].concat(this._batchPairInfos.map(bpi => [bpi.deviceName, bpi.mac, bpi.isFault ? this.STATUS_FAIL : this.STATUS_SUCCESS, bpi.toGroupPath || '', bpi.note].map(data => '"' + data + '"'))) }).pipe(
            map((c: { fileName: string, header: string, metadata?: string[], dataList: string[][], date: string }) => {
                return HelperLib.downloadCsv(this._bulkResulteRef.nativeElement, c.fileName, c.header, c.dataList, c.metadata);
            })
        ).subscribe();
    }

    onBulkRegisterfileChange(event: any): void {
        const file: File = event.target.files[0];
        if (file) {
            HelperLib.blobToOtherType(file, FileReaderParseType.text).pipe(
                map((raw: string) => {
                    const rows: string[] = raw.trim().split(/\n/).map(row =>　row.replace('\r', ''));
                    if (rows.length <= 1) {
                        throw this.TEMPLATE_ERROR;
                    }

                    // check if file format is correct, the first row should match the template file
                    const row0: string[] = rows[0].split(',');
                    for (let i = 0; i < this.TEMPLATE_ROW.length; i++) {
                        if (row0[i] !== this.TEMPLATE_ROW[i]) {
                            throw this.TEMPLATE_ERROR;
                        }
                    }

                    this._dlgPage = DialogPage.submit;

                    const datas: { deviceName: string, mac: string, pairingCode: string, devGroupPath: string, devGroupId?: string }[] = [];
                    for (let i = 1; i < rows.length; i++) {
                        const cols: string[] = rows[i].split(',');
                        cols.forEach(col => col.trim());
                        if (!cols[0] || !cols[2]) {
                            // skip rows without device name or pairing code
                            continue;
                        }

                        datas.push({
                            deviceName: cols[0] || '',
                            mac: cols[1] || '',
                            pairingCode: cols[2] ? cols[2].toLocaleUpperCase() : '',
                            devGroupPath: cols[3] || ''
                        });
                    }

                    return datas;
                }),
                map((datas: { deviceName: string, mac: string, pairingCode: string, devGroupPath: string }[]) => {
                    // get the correct dev-group-id
                    const allDevGroupPathMap: { [devGroupPath: string]: DeviceGroupInfo } = this.devGroupSvc.getAllGroupPath();
                    const allDevGroupPaths: string[] = Object.keys(allDevGroupPathMap);

                    const ret: { deviceName: string, pairingCode: string, devGroupPath: string, devGroupId: string, dev: DeviceInfo }[] = [];
                    datas.forEach(d => {
                        const devGroupPathSplits = d.devGroupPath.split('/').map(path => path.trim());

                        const newDev: DeviceInfo = new DeviceInfo();
                        newDev.virtualName = d.deviceName;
                        newDev.onlineStatus = OnlineStatus.Pairing;
                        newDev.currentSettings[this.constantSvc.DEVKEY_FAKE_DISPLAYNAME] = d.deviceName;

                        if (devGroupPathSplits.length > 1) {
                            // user provides the group path
                            const devGroupPathWithoutTrim = devGroupPathSplits.join('/');
                            ret.push(Object.assign(d, { devGroupId: allDevGroupPathMap[devGroupPathWithoutTrim]?.id, dev: newDev }));
                        }
                        else {
                            // user provides the group name
                            // find the 1st group path which includes the name
                            const foundGroupPath: string = allDevGroupPaths.find(dgPath => dgPath.includes(devGroupPathSplits[0]));
                            ret.push(Object.assign(d, { devGroupId: allDevGroupPathMap[foundGroupPath]?.id, dev: newDev }));
                        }
                    });

                    return ret;
                }),
                concatMap((datas: { deviceName: string, mac: string, pairingCode: string, devGroupPath: string, devGroupId: string, toGroupPath?: string, dev: DeviceInfo }[]) => {
                    const obs: Observable<{
                        isFault: boolean,
                        hasNext: boolean,
                        error?: number | string,
                        errorMessage?: string,
                        note?: string,
                        status?: string,
                        data?: { deviceName: string, pairingCode: string, devGroupPath: string, devGroupId: string, toGroupPath?: string, dev: DeviceInfo }
                    }>[] = [];

                    datas.forEach(data => {
                        obs.push(
                            this.naSvc.createVirtualDevice({ virtualDeviceName: data.deviceName }, this.accountSvc.token).pipe(
                                concatMap((createDeviceRet: IAPIRx<IVirtualDeviceRxData>) => {
                                    this._batchPairProgress = `Pairing device ${data.deviceName} ...`;

                                    if (createDeviceRet.error !== 0) {
                                        throw createDeviceRet.errorMessage;
                                    }

                                    data.dev.virtualId = createDeviceRet.data.virtualDeviceID;

                                    return this.naSvc.pairVirtualDevice({ virtualDeviceID: createDeviceRet.data.virtualDeviceID }, { pairingCode: data.pairingCode }, this.accountSvc.token).pipe(
                                        concatMap((pairDeviceRet: IAPIRx<IVirtualDeviceRxData>) => {
                                            if (pairDeviceRet.error !== 0) {
                                                // delete the virtual device
                                                return this.naSvc.removeVirtualDevice({ virtualDeviceID: createDeviceRet.data.virtualDeviceID }, this.accountSvc.token).pipe(
                                                    map((res: IAPIRx<IVirtualDeviceRxData>) => {
                                                        // don't care about the fail of remove virtual device, just throw the fail result of pairing.
                                                        throw pairDeviceRet.errorMessage;
                                                    })
                                                );
                                            }

                                            data.dev.virtualPairId = pairDeviceRet.data.virtualDevicePairedID;
                                            data.dev.virtualDeviceOwner = pairDeviceRet.data.virtualDeviceOwnerAccountName;
                                            data.dev.virtualDeviceOwnerID = pairDeviceRet.data.virtualDeviceOwnerAccountID;
                                            data.dev.onlineStatus = OnlineStatus.Syncing;
                                            data.dev.groupID = pairDeviceRet.data.virtualDeviceGroupID;
                                            data.dev.isPaired = true;

                                            if (data.devGroupId) {
                                                return this.naSvc.updateDeviceInDeviceGroup({ deviceGroupID: data.devGroupId }, { addList: [createDeviceRet.data.virtualDeviceID] }, this.accountSvc.token).pipe(
                                                    map((updateGroupRet: IAPIRx<any>) => {
                                                        if (updateGroupRet.error !== 0) {
                                                            throw updateGroupRet.errorMessage;
                                                        }

                                                        data.dev.groupID = data.devGroupId;
                                                        data.toGroupPath = data.devGroupPath;

                                                        return { hasNext: true, isFault: false, data: data, note: `Device registered and place under device group '${data.devGroupPath}'` };
                                                    })
                                                );
                                            }

                                            data.toGroupPath = DEVICE_GROUP_NAME_DEFAULT;
                                            return of({ hasNext: true, isFault: false, data: data, note: `'${data.devGroupPath}' not found, device is added to Default group.` });
                                        }),
                                    )
                                }),
                                catchError((err) => of({ hasNext: true, isFault: true, errorMessage: err, data: data }))
                            )
                        );
                    });

                    obs.push(of({ isFault: false, hasNext: false }));

                    return of(true).pipe(
                        concatMap(() => obs),
                        concatAll(),
                        map((res: {
                            isFault: boolean,
                            hasNext: boolean,
                            error?: number | string,
                            errorMessage?: string,
                            data?: { deviceName: string, pairingCode: string, devGroupPath: string, devGroupId: string, toGroupPath?: string, dev: DeviceInfo },
                            note?: string
                        }) => {
                            return Object.assign(res, { isFault: false });
                        })
                    );
                }),
                catchError((err) => {
                    return of({ hasNext: false, isFault: true, errorMessage: err })
        })
            ).subscribe((res: {
                isFault: boolean,
                hasNext: boolean,
                error?: number | string,
                errorMessage?: string,
                data: { deviceName: string, mac: string, pairingCode: string, devGroupPath: string, devGroupId: string, toGroupPath?: string, dev: DeviceInfo },
                note?: string
            }) => {
                if (!res.hasNext) {
                    this._dlgPage = DialogPage.result;
                    // set gthe filePicker value to '' in order to select the same file again.
                    this._filePickerRef.nativeElement.value = '';
                    this._errorMessage = res.errorMessage;
                }
                else {
                    this._batchPairInfos.push({
                        deviceName: res.data?.deviceName,
                        mac: res.data?.mac,
                        pairingCode: res.data?.pairingCode,
                        devGroupId: res.data?.devGroupId,
                        toGroupPath: res.data?.toGroupPath,
                        note: res.note || res.errorMessage,
                        isFault: res.errorMessage ? true : false
                    });

                    this.filterPairInfos();

                    if (!res.errorMessage) {
                        this.onDevicePaired.emit(new VirtualDeviceUIInfo(
                            res.data.dev?.virtualId,
                            res.data.deviceName,
                            res.data.devGroupId ? this.devGroupSvc.getGroupByID(res.data.devGroupId) : this.devGroupSvc.getDefaultGroup(),
                            res.data.pairingCode,
                            res.data.dev?.virtualPairId,
                            VirtualDeviceStatusType.Syncing,
                            VirtualDeviceActions.getActions(VirtualDeviceStatusType.Syncing)
                        ));

                        this.devSvc.addDevice(res.data?.dev, res.data?.deviceName, res.data?.pairingCode).subscribe();
                    }
                }
            });
        }
    }

    filterPairInfos(): void {
        this._displayBatchPairInfos = this._batchPairInfos.filter((bpi => {
            if (this._filters.get(this.STATUS_SUCCESS) && !bpi.isFault) {
                return true;
            }
            if (this._filters.get(this.STATUS_FAIL) && bpi.isFault) {
                return true;
            }

            return false;
        }));
    }

    changeDisplayFilter(status: string, checked: boolean): void {
        this._filters.set(status, checked);
        this.filterPairInfos();
    }
}